import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Minus, ArrowLeft, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PRODUCT_DETAIL } from "@/constants/testIds";
import { toast } from "sonner";

const ProductDetail = () => {
  const { sku } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const { addToCart } = useApp();

  useEffect(() => {
    // Load product on sku change; `api`/setters are stable.
    api.get(`/products/${sku}`)
      .then((r) => setP(r.data))
      .catch(() => setNotFound(true));
  }, [sku]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">Item not found</h1>
      <Link to="/store" className="inline-flex items-center gap-2 mt-6 text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to store
      </Link>
    </div>
  );

  if (!p) return <div className="max-w-6xl mx-auto px-6 py-24 text-muted-foreground">Loading…</div>;

  const addToBasket = () => {
    addToCart(p, qty);
    toast.success(`${p.name} × ${qty} added to basket`);
  };

  return (
    <div data-testid={PRODUCT_DETAIL.page} className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
      <Link to="/store" className="inline-flex items-center gap-2 text-xs uppercase tracking-label text-muted-foreground hover:text-primary">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to store
      </Link>

      <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] overflow-hidden border border-border bg-muted">
            <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <p className="text-xs uppercase tracking-label text-secondary">{p.category}</p>
          <h1 className="display-serif text-4xl md:text-5xl mt-3 leading-tight">{p.name}</h1>
          <p className="font-serif text-3xl mt-6">${p.price.toFixed(2)}</p>
          <p className="mt-6 text-sm leading-relaxed text-muted-foreground">{p.description}</p>

          <div className="mt-10 flex items-center gap-6">
            <div className="flex items-center border border-border">
              <button data-testid={PRODUCT_DETAIL.qtyDec} onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-muted">
                <Minus className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <span className="px-6 text-sm w-12 text-center">{qty}</span>
              <button data-testid={PRODUCT_DETAIL.qtyInc} onClick={() => setQty(Math.min(99, qty + 1))} className="p-3 hover:bg-muted">
                <Plus className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <button
              onClick={addToBasket}
              data-testid={PRODUCT_DETAIL.addToCartBtn}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground text-xs uppercase tracking-label font-bold hover:bg-primary/90 luxe-btn"
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Add to basket
            </button>
          </div>

          <div className="mt-12 pt-8 border-t border-border grid grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-label text-muted-foreground">Sourced</p>
              <p className="text-sm mt-2">Artisan-direct</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-label text-muted-foreground">Shipping</p>
              <p className="text-sm mt-2">3-5 days</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-label text-muted-foreground">Purity</p>
              <p className="text-sm mt-2">100% natural</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
